import moment from "moment";
import momentTimezone from "moment-timezone";
import React, { useContext, useEffect, useState } from "react";
import { getConsolidatedTransactionReport } from "../../../api/report";
import Header from "../../../Components/Header/Header";
import ReactTable from "../../../Components/Table/Table";
import { AppContext } from "../../../Context/AppContext";
import { MODULES, DEFAULT_TIMEZONE } from "./../../../Helpers/Constants";
import { getAccessByModuleName } from "./../../../Routes/Routes";
import { convertDateTime } from "Helpers/date";
import EditIcon from "../../../Assets/images/edit.svg";
import ViewIcon from "../../../Assets/images/error-outline.svg";
import { Link } from "react-router-dom";
import GlobalDateFilters from "Components/GlobalDateFilters/GlobalDateFilters";
import { setAllStore } from "Helpers/setAllStore";
import { safeError } from "utils/logger";
import { CSVLink } from "react-csv";

const ConsolidatedReport = () => {
  const { showToastMessage, modules } = useContext(AppContext);
  const [data, setData] = React.useState([]);
  const [checkAllStore, setCheckAllStore] = useState(true);
  const [assignedStoreList, setAssignedStoreList] = useState([]);
  const [loading, setLoading] = React.useState(false);
  let viewOrderAccess = getAccessByModuleName(modules, MODULES.VIEW_ORDER);
  let editOrderAccess = getAccessByModuleName(modules, MODULES.EDIT_ORDER);
  let reportsAccess = getAccessByModuleName(modules, MODULES.TRANSFER_REPORT);
  const reference = React.useRef(null);
  const [exportCsv, setExportCsv] = React.useState({ header: [], data: [] });
  const [storeTotals, setStoreTotals] = React.useState([]);
  //Get assigned store list from local storage
  const assignStoreData = localStorage.getItem("assignStore")

  //Set assigned store list to state
  useEffect(() => {
    if(assignStoreData) {
      const assignStoreListData = JSON.parse(assignStoreData)
      setAssignedStoreList(assignStoreListData.map(store => store.id))
    }
  }, [assignStoreData]);

  let storeTimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  let defaultStartDate = moment()
    .subtract(7, "days")
    .startOf("day")
    .format("ddd MMM DD, YYYY");
  let defaultEndDate = momentTimezone
    .tz(storeTimezone)
    .startOf("day")
    .format("ddd MMM DD, YYYY");

  const getInitialState = () => {
    try {
      // Retrieve saved filter data from localStorage
      // This key stores the last used filters, pagination, and date range for this report
      const filterDataStr = localStorage.getItem("asyncDataValuesTransferReport");
      
      if (filterDataStr) {
        // Parse the JSON string to get the saved filter data
        const filterData = JSON.parse(filterDataStr);
        
        if (filterData) {
          // Extract saved values from localStorage
          const { currentPage, itemsPerPage, filters, sortBy, date_type, start_date, end_date } = filterData;
          
          // Return state restored from localStorage with fallback defaults
          return {
            defaultFilter: {
              filters: filters || [], // Saved filters or empty array
              pageIndex: currentPage || 0, // Saved page number or first page
              pageSize: itemsPerPage || 10, // Saved page size or default 10
              sortBy: sortBy || [
                // Saved sort configuration or default: sort by updatedAt descending
                {
                  id: "updatedAt",
                  desc: "desc",
                },
              ],
            },
            type: date_type || "order_date", // Date filter type or default to "order_date"
            // Format dates from saved values or use default start/end dates
            fromDate: start_date ? moment(start_date).format("ddd MMM DD, YYYY") : defaultStartDate,
            toDate: end_date ? moment(end_date).format("ddd MMM DD, YYYY") : defaultEndDate,
          };
        }
      }
    } catch (error) {
      // Handle any errors during localStorage parsing (e.g., corrupted data)
      safeError("Error parsing localStorage data:", error);
    }
    
    // Return default state if no saved data exists or if parsing failed
    return {
      defaultFilter: {
        pageIndex: 0, // Start at first page
        pageSize: 10, // Default page size
        filters: [], // No filters applied
        sortBy: [
          // Default sort: by updatedAt in descending order (newest first)
          {
            id: "updatedAt",
            desc: "desc",
          },
        ],
      },
      type: "order_date", // Default date filter type
      fromDate: defaultStartDate, // Default start date (7 days ago)
      toDate: defaultEndDate, // Default end date (today)
    };
  };

  const initialState = getInitialState();
  const [defaultFilter, setDefaultFilter] = React.useState(initialState.defaultFilter);
  const [type, setType] = useState(initialState.type);
  const [fromDate, setFromDate] = useState(initialState.fromDate);
  const [toDate, setToDate] = useState(initialState.toDate);

  const [pageCount, setPageCount] = React.useState(1);

  const handleClearSearch = async (event) => {
    event.preventDefault();
    setFromDate(defaultStartDate);
    setToDate(defaultEndDate);
    fetchData({
      ...defaultFilter,
      fromDateDefault: defaultStartDate,
      toDateDefault: defaultEndDate,
    });
  };

  // Set Async Data
  const setAsyncData = (data) => {
    let asyncDataValues = JSON.stringify(data);
    localStorage.setItem("asyncDataValuesTransferReport", asyncDataValues);
  };

  // Date Validation
  const dateValidation = (fromDate, toDate) => {
    let startDate = moment(fromDate).format("x");
    let endDate = moment(toDate).format("x");
    if (startDate > endDate) {
      showToastMessage(
        undefined,
        "Start date should not be greater than from end date",
        "error",
        false
      );
      return false;
    }
    return true;
  };

  const fetchData = async ({
    pageIndex,
    pageSize,
    filters,
    fromDateDefault,
    toDateDefault,
    sortBy,
  }) => {
    if (
      !dateValidation(
        fromDateDefault ? fromDateDefault : fromDate,
        toDateDefault ? toDateDefault : toDate
      )
    ) {
      return false;
    }
    setAllStore(checkAllStore);
    let startDate = fromDateDefault ? fromDateDefault : fromDate;
    startDate = moment(startDate).format("YYYY-MM-DD");
    let endDate = toDateDefault ? toDateDefault : toDate;
    endDate = moment(endDate).format("YYYY-MM-DD");
    setDefaultFilter({ pageSize, pageIndex, filters });
    setLoading(true);
    const postData = {
      currentPage: pageIndex,
      itemsPerPage: pageSize,
      start_date: startDate,
      end_date: endDate,
      date_type: type,
      filters,
      sortBy,
    };
    setAsyncData({ ...postData, checkAllStore });

    if (reportsAccess && reportsAccess.permissions.read_access) {
      const response = await getConsolidatedTransactionReport(postData);
      if (response && response.status === 200) {
        setData(response.data.data.rows);
        setStoreTotals(response.data.data.store_totals || []);
        setPageCount(Math.ceil(response.data.data.count / pageSize));
      } else {
        showToastMessage(undefined, response?.data?.message, "error", false);
      }
    }
    setLoading(false);
    setDefaultFilter({ pageSize, pageIndex, filters, sortBy });
    //when the all store checkbox is checked and we get all store data(after the API call) then we set the selected store id=dropdown selected store in local storage and this logic writes in setAllStore  function
    if(checkAllStore){ setAllStore(false) }
  };

  const handleExport = async () => {
    // the last search's request, so the CSV matches the table and totals on screen
    const { checkAllStore: allStore, ...lastSearch } = JSON.parse(localStorage.getItem("asyncDataValuesTransferReport"));
    setAllStore(allStore);
    const response = await getConsolidatedTransactionReport({ ...lastSearch, isExport: true });
    setAllStore(false);
    if (response && response.status === 200) {
      setExportCsv(response.data.data);
      if (!response.data.data.data.length) showToastMessage(undefined, "There is no data to export");
    } else {
      showToastMessage(undefined, response?.data?.message, "error", false);
    }
  };

  // CSVLink builds the blob href in render, so click after the re-render, not in handleExport
  useEffect(() => {
    if (exportCsv.data.length > 0) reference.current?.link?.click();
  }, [exportCsv]);

  const columns = React.useMemo(() => [
    {
      Header: "Order Number",
      accessor: "order_number",
      isVisible: true,
      Cell: ({ row }) => `${row.original?.order_number ?? "-"}`,
    },
    {
      Header: "Store Name",
      accessor: "transfer_order_store",
      isVisible: true,
      disableSortBy: true,
      Cell: ({ row }) => `${row.original?.store?.title ?? "-"}`,
    },
    {
      Header: "Original Store Name",
      accessor: "original_store_name",
      isVisible: true,
      disableSortBy: true,
      colWidth: 250,
      Cell: ({ row }) => `${row.original?.original_store?.title ?? "-"}`,
    },
    {
      Header: "Paid By",
      accessor: "paid_by_store_name",
      isVisible: true,
      disableSortBy: true,
      disableFilters: true,
      colWidth: 250,
      Cell: ({ row }) => `${row.original?.paid_by_store_name ?? "-"}`,
    },
    {
      Header: "Direction",
      accessor: "direction",
      isVisible: true,
      disableSortBy: true,
      disableFilters: true,
      colWidth: 250,
    },
    {
      Header: "Delivery Date",
      accessor: "delivery_date_time",
      isVisible: true,
      placeholder: "Delivery Date",
      Cell: ({ value, row, column }) => {
        return `${
          row.original.delivery_date_time &&
          row.original.delivery_date_time !== null
            ? row.original.delivery_time_type === "Anytime"
              ? convertDateTime(row.original.delivery_date_time, 'iso', "ccc LLL dd, yyyy") +
                " " +
                row.original.delivery_time_type
              : convertDateTime(row.original.delivery_date_time, 'iso', "ccc LLL dd, yyyy, hh:mm a")
            : ""
        }`;
      },
      disableFilters: true,
    },
    {
      Header: "Order Date",
      accessor: "createdAt",
      isVisible: true,
      placeholder: "Order Date",
      Cell: ({ value, row, column }) => {
        return `${
          row.original.createdAt && row.original.createdAt !== null
            ? convertDateTime(row.original.createdAt, 'iso', "ccc LLL dd, yyyy, hh:mm a")
            : ""
        }`;
      },
      disableFilters: true,
    },
    {
      Header: "Total Amount",
      accessor: "total",
      isVisible: true,
      disableFilters: false,
      disableSortBy: false,
      Cell: ({ value, row, column }) => {
        return (
          <div className="text-center">${row.original.total.toFixed(2)}</div>
        );
      },
    },
    ...[["Tax", "tax"], ["Fee", "fee"], ["Transfer Amount", "transfer_amount"]].map(([Header, accessor]) => ({
      Header, accessor, isVisible: true, disableFilters: true, disableSortBy: true,
      Cell: ({ value }) => <div className="text-center">${(value ?? 0).toFixed(2)}</div>,
    })),
    {
      Header: "Type",
      accessor: "report_type",
      isVisible: true,
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }) => row.original?.report_type ?? "-",
    },
    {
      Header: "Actions",
      accessor: "id",
      isVisible: true,
      disableSortBy: true,
      Cell: ({ value, row, column }) => {
        //check current order is belongs to assigned store list or not
        const isBelongsToAssignedStore = assignedStoreList.includes(row.original.store_id)
        return (
          <div className="d-flex justify-content-center align-items-center gap-0">
            {viewOrderAccess && viewOrderAccess.permissions.read_access && (
              <Link to={`/admin/orders/view/${row.original.id}`}>
                <img
                  className={`cursor-pointer mr-2 view-icon`}
                  src={ViewIcon}
                  alt="View Order"
                />
              </Link>
            )}
            {editOrderAccess && editOrderAccess.permissions.write_access && isBelongsToAssignedStore && (
              <Link to={`/admin/orders/edit/${row.original.id}`}>
                <img
                  className={`cursor-pointer`}
                  src={EditIcon}
                  alt="Edit Order"
                />
              </Link>
            )}
          </div>
        );
      },
    },
  ]);

  return (
    <div>
      <Header title="Transfer Report" />
      <div className="row mb-5">
        <div className="col-sm-12 col-xs-12 m-0 p-0">
          <GlobalDateFilters
            isDateFilter
            isSearchIcon
            isClearSearchBtn
            isFilterByOption
            isAllCheckStoreBnt
            isExportBtn={!!(reportsAccess && reportsAccess.permissions.read_access)}
            handleExport={handleExport}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fetchData={() =>
              fetchData({
                ...defaultFilter,
              })
            }
            handleClearSearch={handleClearSearch}
            checkAllStore={(data) => {
              setCheckAllStore(data);
            }}
            defaultSetCheckAllStore={checkAllStore}
            type={type}
            setType={setType}
            key={"consolidated-report"}
          />
        </div>
      </div>
      <div>
        <CSVLink ref={reference} style={{ display: "none" }} data={exportCsv.data} headers={exportCsv.header} filename="transferReport.csv" />
        {storeTotals.length > 0 && (
          <div className="table-responsive mb-4">
            <table className="table">
              <thead>
                <tr><th>Store Name</th><th>Orders</th><th>Total</th><th>Tax</th><th>Fee</th><th>Transfer Amount</th></tr>
              </thead>
              <tbody>
                {storeTotals.map((store) => (
                  <tr key={store.store_id}>
                    <td>{store.store_name}</td><td>{store.order_count}</td><td>${store.total.toFixed(2)}</td>
                    <td>${(store.tax ?? 0).toFixed(2)}</td><td>${(store.fee ?? 0).toFixed(2)}</td><td>${(store.transfer_amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ReactTable
          columns={columns}
          data={data}
          loading={loading}
          fetchData={fetchData}
          pageCount={pageCount}
          defaultFilter={defaultFilter}
          noDataText={"There is no data to display"}
          displayGlobalSearch={false}
          displayColumnVisibility={false}
          displayLastColumnSorting={false}
        />
      </div>
    </div>
  );
};
export default ConsolidatedReport;
